import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X, Lock, CalendarCheck, Heart } from "lucide-react";
import { CookieBanner } from "./CookieBanner";

const NAV = [
  ["/", "Home"], ["/lo-studio", "Lo studio"], ["/servizi", "Servizi"], ["/news", "News e circolari"],
  ["/scadenze", "Scadenze"], ["/domande-frequenti", "FAQ"], ["/contatti", "Contatti"],
];

export const Brand = ({ light }) => (
  <Link to="/" className="flex items-center gap-3 tap" data-testid="brand-link" aria-label="Studio Diolosa – Home">
    <span className={`grid h-10 w-10 place-items-center border ${light ? "border-white/40 text-white" : "border-primary text-primary"} font-display text-lg font-semibold`}>SD</span>
    <span className="leading-tight">
      <span className={`block font-display text-lg font-semibold ${light ? "text-white" : "text-primary"}`}>Studio Diolosa</span>
      <span className={`block text-[11px] uppercase tracking-[0.18em] ${light ? "text-white/70" : "text-muted-foreground"}`}>Commercialisti</span>
    </span>
  </Link>
);

function Header() {
  const [open, setOpen] = useState(false);
  const link = ({ isActive }) => `tap inline-flex items-center px-2 text-sm font-medium transition-colors hover:text-primary ${isActive ? "text-primary underline underline-offset-8 decoration-[hsl(var(--accent))] decoration-2" : "text-foreground/75"}`;
  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur-md">
      <div className="container-x flex h-[72px] items-center justify-between gap-4">
        <Brand />
        <nav className="hidden xl:flex items-center gap-3" aria-label="Navigazione principale">
          {NAV.map(([to, l]) => <NavLink key={to} to={to} end={to === "/"} className={link} data-testid={`nav-${to.replace(/\//g, "") || "home"}`}>{l}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/appuntamento" className="hidden md:inline-flex tap items-center gap-2 px-4 text-sm font-semibold border border-primary text-primary rounded-md hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="header-appointment-btn">
            <CalendarCheck className="h-4 w-4" aria-hidden /> Appuntamento
          </Link>
          <Link to="/accedi" className="tap inline-flex items-center gap-2 px-4 text-sm font-semibold rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid="header-portal-btn">
            <Lock className="h-4 w-4" aria-hidden /> <span className="whitespace-nowrap">Area clienti</span>
          </Link>
          <button className="xl:hidden tap min-w-[44px] grid place-items-center rounded-md border" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? "Chiudi menu" : "Apri menu"} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav id="mobile-nav" className="xl:hidden border-t bg-background" aria-label="Navigazione mobile" data-testid="mobile-nav">
          <ul className="container-x py-3">
            {NAV.map(([to, l]) => (
              <li key={to}><NavLink to={to} end={to === "/"} onClick={() => setOpen(false)} className="flex tap items-center py-3 text-base font-medium border-b border-border/60" data-testid={`mobile-nav-${to.replace(/\//g, "") || "home"}`}>{l}</NavLink></li>
            ))}
            <li className="pt-4 grid gap-3 sm:grid-cols-2 pb-2">
              <Link to="/appuntamento" onClick={() => setOpen(false)} className="tap flex items-center justify-center rounded-md border border-primary font-semibold">Richiedi un appuntamento</Link>
              <Link to="/accedi" onClick={() => setOpen(false)} className="tap flex items-center justify-center rounded-md bg-primary text-primary-foreground font-semibold">Accedi all'area clienti</Link>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground grain">
      <div className="container-x py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2 space-y-4">
          <Brand light />
          <p className="text-sm text-white/75 max-w-md">Via Cavour 9, 33097 Spilimbergo (Pordenone), Italia</p>
          <p className="text-xs text-white/60">Email e telefono sono disponibili nella pagina <Link className="underline" to="/contatti">Contatti</Link>.</p>
        </div>
        <nav aria-label="Collegamenti del sito" className="text-sm space-y-2">
          {NAV.slice(1).map(([to, l]) => <Link key={to} to={to} className="block text-white/80 hover:text-white">{l}</Link>)}
        </nav>
        <nav aria-label="Informazioni legali" className="text-sm space-y-2">
          <Link to="/privacy" className="block text-white/80 hover:text-white">Privacy</Link>
          <Link to="/cookie" className="block text-white/80 hover:text-white" data-testid="footer-cookie-link">Cookie e preferenze</Link>
          <Link to="/richieste-dati" className="block text-white/80 hover:text-white">Richieste sui dati personali</Link>
          <Link to="/newsletter" className="block text-white/80 hover:text-white">Newsletter</Link>
          <Link to="/guida-portale" className="block text-white/80 hover:text-white">Guida all'area clienti</Link>
        </nav>
      </div>
      <div className="border-t border-white/10"><div className="container-x py-5 text-xs text-white/60 flex flex-wrap gap-2 justify-between">
        <span>© {new Date().getFullYear()} Studio Diolosa – Commercialisti</span><span>P. IVA: [da inserire]</span>
        <a href="https://telcavoip.ai/it/services/webdesign" target="_blank" rel="noopener" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1 text-white/80 hover:text-white hover:border-white/40 transition-colors" data-testid="footer-made-by-badge">
          Made with <Heart className="h-3.5 w-3.5 fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" aria-label="love" /> by TelcaVoIP International
        </a>
      </div></div>
    </footer>
  );
}

export default function SiteLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="skip-link">Vai al contenuto</a>
      <Header />
      <main id="main" className="flex-1" tabIndex={-1}><Outlet /></main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

export const PageHero = ({ eyebrow, title, children }) => (
  <section className="bg-primary text-primary-foreground grain">
    <div className="container-x py-14 sm:py-20 fade-up">
      {eyebrow && <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[hsl(var(--accent))] font-mono-ui">{eyebrow}</p>}
      <h1 className="mt-3 text-4xl sm:text-5xl font-semibold max-w-3xl">{title}</h1>
      {children && <div className="mt-5 max-w-2xl text-base md:text-lg text-white/80">{children}</div>}
    </div>
  </section>
);
